import { Stack, Tabs, Tab } from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useCallback } from 'react';

const VmSnapshotsTypes = [
  { type: 'vm-based', label: 'Vm-based snapshots' },
  { type: 'database-pitr', label: 'Database PiTR' },
] as const;
const DBPiTRSnapshotsTypes = [
  { type: 'rds-based-snapshots', label: 'RDS-based snapshots' },
  { type: 'database-pitr', label: 'Database PiTR' },
] as const;
const BigQuerySnapshotsTypes = [
  { type: 'rds-based-snapshots', label: 'Eon snapshots' },
  { type: 'database-pitr', label: 'BigQuery PiTR' },
] as const;
export type SnapshotsType =
  | (typeof VmSnapshotsTypes)[number]['type']
  | (typeof DBPiTRSnapshotsTypes)[number]['type'];

const snapshotTypesMap: Partial<
  Record<
    ResourceType,
    {
      types: readonly { type: SnapshotsType; label: string }[];
      defaultType: SnapshotsType;
    }
  >
> = {
  [ResourceType.AwsRds]: {
    types: [...DBPiTRSnapshotsTypes],
    defaultType: 'database-pitr',
  },
  [ResourceType.AzureSqlVirtualMachine]: {
    types: [...VmSnapshotsTypes],
    defaultType: 'vm-based',
  },
  [ResourceType.GcpBigQuery]: {
    types: [...BigQuerySnapshotsTypes],
    defaultType: 'database-pitr',
  },
} as const;

export function SnapshotsTypesTabs({
  resourceType,
  onTypeSelected,
  snapshotType,
}: {
  resourceType: ResourceType;
  onTypeSelected: (type: SnapshotsType) => void;
  snapshotType?: SnapshotsType;
}) {
  const snapshotsTypes = snapshotTypesMap[resourceType];
  if (!snapshotsTypes) {
    return null;
  }

  return (
    <Stack
      direction='row'
      alignItems='center'
      padding={'16px 40px 0'}
      minHeight='50px'
      borderBottom='1px solid var(--mui-palette-divider)'
    >
      <Tabs
        onChange={(e, value) => onTypeSelected(value)}
        value={snapshotType ?? snapshotsTypes.defaultType}
        className='min-h-[50px] border-b-0'
        sx={{ '.MuiTabs-scroller': { display: 'flex' } }}
      >
        {snapshotsTypes.types.map(({ type, label }) => (
          <Tab
            value={type}
            label={label}
            key={type}
            className='text-[14px] font-normal px-[12px]'
            disableRipple
          />
        ))}
      </Tabs>
    </Stack>
  );
}

export const useCallbackSnapshotsTypesTabs = (
  isVmBaseResourceType: boolean,
  isContinuousSupported: boolean,
  isBigQueryNativePitr: boolean
): (() => SnapshotsType | undefined) => {
  return useCallback(() => {
    if (isVmBaseResourceType) {
      return 'vm-based';
    }
    if (isBigQueryNativePitr && isContinuousSupported) {
      return 'database-pitr';
    }
    if (isBigQueryNativePitr) {
      // todo: change this to proper type as it's not rds
      return 'rds-based-snapshots';
    }
    if (isContinuousSupported) {
      return 'rds-based-snapshots';
    }
    return undefined;
  }, [isVmBaseResourceType, isContinuousSupported, isBigQueryNativePitr]);
};
